import type {
  EconomicCatalogLifecycle,
  EconomicCoefficient,
  EconomicSourceCatalogEntry,
  EconomicSourceCatalogSnapshot,
} from "../../types/economicContribution";
import { ECONOMIC_CONTRIBUTION_SCHEMA_VERSION } from "../../types/economicContribution";
import { validateEconomicCatalogEntrySemanticAudit } from "./sourceSemanticAudit";

export const ECONOMIC_SOURCE_POLICY_ID = "economic-source-policy-v3" as const;

const HAMM_USC_SOURCE = {
  id: "hamm-usc-data-center-employment-forecast-2025",
  title: "Data Center Employment Forecast Analysis",
  publisher:
    "Hamm Institute for American Energy / USC Zage Business of Energy Initiative",
  url: "https://hamminstitute.org/site-files/documents/data_center_workforce.pdf",
  publicationDate: "2025-12-03",
  accessedAt: "2026-08-26",
  excerpt:
    "Construction requirements range from 0.7-2.0 workers per MW, while operations employ 0.15-0.35 full-time equivalents (FTEs) per MW once online.",
  locator: "Executive summary, p. 1; coefficient tables, pp. 9–10",
  excerptKind: "verbatim-quote",
  evidenceRole: "numeric-basis",
  page: "pp. 1, 3–4, 8–10",
  notes:
    "Size-banded direct onsite operations and construction workforce parameters; underlying facility records mix reported and analyst-estimated MW.",
} as const;

const UPTIME_STAFFING_SOURCE = {
  id: "uptime-data-center-staffing-2015",
  title: "Proper Data Center Staffing is Key to Reliable Operations",
  publisher: "Uptime Institute",
  url: "https://journal.uptimeinstitute.com/data-center-staffing/",
  publicationDate: "2015-03-12",
  accessedAt: "2026-08-26",
  excerpt:
    "minimum of one to two qualified operators on site 24 hours per day, 7 days per week, 365 days per year",
  locator: "Staffing 24x7 section, lines 101–106",
  excerptKind: "verbatim-quote",
  evidenceRole: "numeric-basis",
  notes:
    "Recommends one to two qualified operators onsite continuously for Tier III/IV business objectives; does not quantify IT, security, management, or contractor staffing.",
} as const;

const KERN_CEC_SOURCE = {
  id: "kern-edc-rb-inyokern-cec-2026",
  title: "RB Inyokern Data Center letter of support, Docket 26-SPPE-01",
  publisher: "Kern Economic Development Corporation",
  url: "https://efiling.energy.ca.gov/GetDocument.aspx?DocumentContentId=107381&tn=270227",
  publicationDate: "2026-05-22",
  accessedAt: "2026-08-26",
  excerpt:
    "99 MW; 590 peak construction workers; 30–60 permanent jobs; $250 million investment.",
  locator: "Letter body, unnumbered p. 1 (PDF p. 3)",
  excerptKind: "table-transcription",
  evidenceRole: "independent-cross-check",
  normalizedChecks: [
    {
      use: "operations-fte-per-mw",
      mw: 99,
      low: 30 / 99,
      high: 60 / 99,
      units: "workers-per-utility-service-mw",
    },
    {
      use: "construction-peak-workers-per-mw",
      mw: 99,
      low: 590 / 99,
      high: 590 / 99,
      units: "workers-per-utility-service-mw",
    },
  ],
  notes:
    "Project-specific proposed figures in a California Energy Commission docket; the letter's 99 MW basis is resolved by the companion docket appendix recorded in the semantic audit, and employment is not verified actual.",
} as const;

const TERAWULF_KY_PSC_SOURCE = {
  id: "terawulf-kentucky-psc-2026",
  title: "Motion for Expedited Review, Kentucky PSC Case 2026-00115",
  publisher:
    "TeraWulf Inc. filing in the Kentucky Public Service Commission docket",
  url: "https://psc.ky.gov/pscecf/2026-00115/mkurtz%40bkllawfirm.com/05272026041401/TeraWulf_Motion_Exp._Review__2026-00115.pdf",
  publicationDate: "2026-05-27",
  accessedAt: "2026-08-26",
  excerpt:
    "482 MW; 800–1,000 peak construction jobs; 80–100 permanent data-center jobs.",
  locator: "Motion, pp. 2 and 4",
  excerptKind: "table-transcription",
  evidenceRole: "independent-cross-check",
  normalizedChecks: [
    {
      use: "operations-fte-per-mw",
      mw: 482,
      low: 80 / 482,
      high: 100 / 482,
      units: "workers-per-utility-service-mw",
    },
    {
      use: "construction-peak-workers-per-mw",
      mw: 482,
      low: 800 / 482,
      high: 1_000 / 482,
      units: "workers-per-utility-service-mw",
    },
  ],
  notes:
    "Project-specific projected figures in a public utility-commission docket; commitments are not verified actual employment.",
} as const;

const AMAZON_GILROY_CEC_SOURCE = {
  id: "amazon-gilroy-cec-sppe-2021",
  title: "Gilroy Backup Generating Facility revised project description",
  publisher: "Amazon Data Services, Inc. (California Energy Commission docket filing)",
  url: "https://efiling.energy.ca.gov/GetDocument.aspx?DocumentContentId=72645&tn=239193",
  publicationDate: "2021-08-05",
  accessedAt: "2026-08-26",
  excerpt:
    "95.84 MW maximum full-buildout electrical demand; 75 average and 110 peak construction workers per phase; 21-month Phase I/II period; up to 50 full-time employees.",
  locator: "Project description, pp. 12, 23, and 26",
  excerptKind: "table-transcription",
  evidenceRole: "numeric-basis",
  normalizedChecks: [
    {
      use: "construction-average-to-peak-ratio",
      mw: 96,
      low: 75 / 110,
      high: 75 / 110,
      units: "ratio",
    },
    {
      use: "construction-duration-years",
      mw: 96,
      low: 21 / 12,
      high: 21 / 12,
      units: "years",
    },
  ],
  notes:
    "Applicant estimates in a public CEC docket; the application was later withdrawn and the proceeding terminated, so values are planning quantities, not verified outcomes.",
} as const;

const VANTAGE_CA3_CEC_SOURCE = {
  id: "vantage-ca3-cec-final-eir-2022",
  title: "CA3 Backup Generating Facility Final Environmental Impact Report",
  publisher: "California Energy Commission staff",
  url: "https://efiling.energy.ca.gov/GetDocument.aspx?DocumentContentId=75956&tn=242454",
  publicationDate: "2022-03-28",
  accessedAt: "2026-08-26",
  excerpt:
    "Phase I: 14 months, 100 average, 150 peak. Phase II: 11 months, 80 average, 200 peak. Combined: 90 average and 175 peak; construction begins January 2022 and operation begins in early 2025.",
  locator: "Final EIR, Transportation §4.17, p. 4.17-6",
  excerptKind: "table-transcription",
  evidenceRole: "independent-cross-check",
  normalizedChecks: [
    {
      use: "construction-average-to-peak-ratio",
      mw: 96,
      low: 90 / 175,
      high: 90 / 175,
      units: "ratio",
    },
    {
      use: "construction-duration-years",
      mw: 96,
      low: 3,
      high: 3,
      units: "years",
    },
  ],
  notes:
    "CEC staff's project-level EIR summarizes applicant construction phasing; dates imply roughly three elapsed years from construction start to initial commercial operation.",
} as const;

const COMMON_PROPOSED = {
  schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
  lifecycle: "proposed" as const,
  geographicScope: "US" as const,
  proposedBy: "engineering-research-draft",
  proposedAt: "2026-08-26",
  nextReviewAt: "2027-08-26",
};

/**
 * Policy-validated screening catalog. Entries are usable only while they pass
 * the versioned structural/evidentiary policy below. Named review remains an
 * optional override path, not a production dependency.
 */
const ECONOMIC_SOURCE_CATALOG_CANDIDATE: EconomicSourceCatalogSnapshot = {
  schemaVersion: ECONOMIC_CONTRIBUTION_SCHEMA_VERSION,
  catalogVersion: "economic-sources-candidate-2026-08-26",
  // Date-only on purpose. This denotes the calendar day the catalog was cut — the
  // sibling catalogVersion slug carries the same day — not a wall-clock instant.
  // Written as "…T00:00:00.000Z" it renders as 25 August for every viewer west of
  // Greenwich, because the vintage formatter (correctly) treats anything carrying a
  // time component as an instant and shows it in the viewer's own zone.
  createdAt: "2026-08-26",
  entries: [
    {
      ...COMMON_PROPOSED,
      id: "operations-coverage-floor-uptime-v1",
      coefficient: {
        kind: "operations-coverage-floor",
        onsitePositionsLow: 1,
        onsitePositionsHigh: 2,
        weeklyCoverageHours: 168,
        annualHoursPerFte: 2_080,
      },
      sources: [
        UPTIME_STAFFING_SOURCE,
        KERN_CEC_SOURCE,
        TERAWULF_KY_PSC_SOURCE,
      ],
      publicationVintage: "2015",
      facilityScope: "Critical facilities with Tier III/IV business objectives",
      nativeUnits:
        "qualified onsite operators per continuously staffed position",
      jobDefinition:
        "Direct qualified facility operators physically present onsite",
      timeBasis: "24 hours/day, 7 days/week, 365 days/year",
      transformation:
        "Each continuously covered position is converted to FTE as 168 weekly coverage hours ÷ 40 paid hours per FTE-week (equivalent to 8,736 annual hours ÷ 2,080). Leave/backfill is not added.",
      permittedUses: ["direct-jobs-screening:facility-operations-floor"],
      limitations: [
        "Covers qualified facility operators only, not the total permanent onsite workforce.",
        "Does not include leave, training, backfill, management, IT, security, janitorial, or contractors.",
        "Applicability depends on the project requiring continuous qualified onsite coverage.",
      ],
    },
    {
      ...COMMON_PROPOSED,
      id: "operations-size-band-intensity-hamm-usc-v1",
      coefficient: {
        kind: "size-band-intensity",
        use: "operations-fte-per-mw",
        mwBasis: "utility-service",
        bands: [
          {
            id: "large",
            label: "Large (50–<100 MW)",
            minMwInclusive: 50,
            maxMwExclusive: 100,
            low: 0.2,
            high: 0.61,
          },
          {
            id: "hyperscale",
            label: "Hyperscale (100+ MW)",
            minMwInclusive: 100,
            maxMwExclusive: null,
            low: 0.15,
            high: 0.3,
          },
        ],
      },
      sources: [HAMM_USC_SOURCE, KERN_CEC_SOURCE, TERAWULF_KY_PSC_SOURCE],
      publicationVintage: "2024–2025 benchmarks; published 2025",
      facilityScope:
        "US hyperscale and colocation facilities with reported or estimated total utility capacity",
      nativeUnits: "direct onsite operations FTE per MW",
      nativeDenominator: "total utility capacity MW",
      jobDefinition: "Permanent direct onsite operations employment",
      timeBasis: "Operating-year stock employment",
      transformation:
        "Apply the published low/high operations intensity for the applicable size band to total utility capacity MW, then compare with the independently sourced coverage floor.",
      permittedUses: ["direct-jobs-screening:operations-size-band"],
      limitations: [
        "The source pairs some disclosed job counts with separately reported or analyst-estimated MW.",
        "The source notes that public announcements may include engineering, administrative, or regional support roles.",
        "Projects below 50 MW remain outside the runtime domain because the catalog lacks an independent project-specific MW/jobs cross-check for those bands.",
        "The 50–100 MW and 100+ MW ranges conservatively encompass the independent California and Kentucky project disclosures rather than hiding their disagreement with the national benchmark.",
        "Operating-model, workload, rack, building, and cooling adjustments are not independently identified and remain narrative sensitivities.",
      ],
    },
    {
      ...COMMON_PROPOSED,
      id: "construction-size-band-intensity-hamm-usc-v1",
      coefficient: {
        kind: "size-band-intensity",
        use: "construction-peak-workers-per-mw",
        mwBasis: "utility-service",
        bands: [
          {
            id: "large",
            label: "Large (50–<100 MW)",
            minMwInclusive: 50,
            maxMwExclusive: 100,
            low: 0.8,
            high: 6.0,
          },
          {
            id: "hyperscale",
            label: "Hyperscale (100+ MW)",
            minMwInclusive: 100,
            maxMwExclusive: null,
            low: 0.7,
            high: 2.1,
          },
        ],
      },
      sources: [HAMM_USC_SOURCE, KERN_CEC_SOURCE, TERAWULF_KY_PSC_SOURCE],
      publicationVintage: "2024–2025 benchmarks; published 2025",
      facilityScope:
        "US data-center construction projects by total utility capacity",
      nativeUnits: "direct onsite construction workers per MW",
      nativeDenominator: "total utility capacity MW",
      jobDefinition:
        "Peak direct onsite construction workers; excludes indirect and induced jobs",
      timeBasis: "Peak construction workforce",
      transformation:
        "Multiply total utility capacity MW by the published low/high peak construction intensity for the applicable size band.",
      permittedUses: ["construction-jobs-screening:peak-workers"],
      limitations: [
        "Project examples and disclosure conventions vary; construction and operations are not always separated.",
        "Projects below 50 MW remain outside runtime use because no independent same-unit MW/peak-worker cross-check is cataloged for those bands.",
        "The supported ranges encompass the independent 99 MW California and 482 MW Kentucky peak-worker disclosures and are intentionally broad.",
        "Whole-campus and current-phase MW must not be mixed.",
      ],
    },
    {
      ...COMMON_PROPOSED,
      id: "construction-average-to-peak-public-record-v1",
      coefficient: { kind: "construction-average-to-peak-ratio", low: 0.5, high: 0.7 },
      sources: [AMAZON_GILROY_CEC_SOURCE, VANTAGE_CA3_CEC_SOURCE],
      publicationVintage: "2021–2022 public project filings",
      facilityScope: "Approximately 96 MW US data-center construction projects",
      nativeUnits: "average annual workforce / peak workforce",
      jobDefinition: "Direct onsite construction workforce",
      timeBasis: "Average annual workforce over the construction duration",
      transformation: "Average onsite workers = peak onsite worker range × the policy-valid 0.50–0.70 average-to-peak range.",
      permittedUses: [
        "construction-jobs-screening:average-workers",
        "construction-jobs-screening:job-years",
      ],
      limitations: [
        "The range is derived from two California project filings near 96 MW and may not represent every project size, delivery model, or labor market.",
        "The range deliberately encompasses both observed filing ratios rather than selecting a single representative scalar.",
      ],
    },
    {
      ...COMMON_PROPOSED,
      id: "construction-duration-public-record-v1",
      coefficient: { kind: "construction-duration-years", low: 1.75, high: 3 },
      sources: [AMAZON_GILROY_CEC_SOURCE, VANTAGE_CA3_CEC_SOURCE],
      publicationVintage: "2021–2022 public project filings",
      facilityScope: "Approximately 96 MW US data-center construction projects",
      nativeUnits: "years",
      jobDefinition: "Direct onsite construction period",
      timeBasis: "Start of construction through commercial operation",
      transformation:
        "Direct job-years = average onsite worker range × the policy-valid 1.75–3.00 year elapsed-duration range.",
      permittedUses: [
        "construction-jobs-screening:duration",
        "construction-jobs-screening:job-years",
      ],
      limitations: [
        "The range reflects two project filings near 96 MW and is not a schedule commitment for the subject site.",
        "The high end uses elapsed time from stated construction start to commercial operation; periods of lower or paused activity may be included.",
      ],
    },
  ],
};

export function effectiveCatalogLifecycle(
  entry: EconomicSourceCatalogEntry,
  asOf: Date = new Date(),
): EconomicCatalogLifecycle {
  if (entry.lifecycle !== "approved-current") return entry.lifecycle;
  if (!entry.nextReviewAt) return entry.lifecycle;
  const reviewTime = Date.parse(entry.nextReviewAt);
  return Number.isFinite(reviewTime) && reviewTime < asOf.getTime()
    ? "approved-stale"
    : entry.lifecycle;
}

export function isCatalogEntryRuntimeUsable(
  entry: EconomicSourceCatalogEntry,
  asOf: Date = new Date(),
): boolean {
  const lifecycle = effectiveCatalogLifecycle(entry, asOf);
  const namedReview =
    typeof entry.reviewedBy === "string" &&
    entry.reviewedBy.trim().length > 0 &&
    typeof entry.reviewedAt === "string" &&
    Number.isFinite(Date.parse(entry.reviewedAt));
  const policyValidation =
    entry.validationPolicyId === ECONOMIC_SOURCE_POLICY_ID &&
    typeof entry.validatedAt === "string" &&
    Number.isFinite(Date.parse(entry.validatedAt));
  return (
    (lifecycle === "approved-current" || lifecycle === "approved-stale") &&
    (namedReview || policyValidation) &&
    validateEconomicCatalogEntry(entry).length === 0 &&
    validateEconomicCatalogPolicyPromotion(entry).length === 0 &&
    validateEconomicCatalogEntrySemanticAudit(entry).length === 0
  );
}

export function validateEconomicCatalogEntry(
  entry: EconomicSourceCatalogEntry,
): string[] {
  const errors: string[] = [];
  if (!entry.id.trim()) errors.push("id is required.");
  if (entry.sources.length === 0)
    errors.push("at least one source is required.");
  for (const source of entry.sources) {
    if (!source.id.trim() || !source.title.trim() || !source.publisher.trim())
      errors.push("source identity is incomplete.");
    if (!/^https:\/\//.test(source.url))
      errors.push(`${source.id || "source"}: HTTPS source URL is required.`);
    if (!Number.isFinite(Date.parse(source.accessedAt)))
      errors.push(`${source.id || "source"}: accessedAt must be a date.`);
    if (!source.excerpt.trim())
      errors.push(
        `${source.id || "source"}: stored source excerpt is required.`,
      );
    if (!source.locator.trim())
      errors.push(`${source.id || "source"}: source locator is required.`);
    if (source.excerpt.length > 280)
      errors.push(
        `${source.id || "source"}: excerpt must be 280 characters or fewer.`,
      );
  }
  if (!entry.publicationVintage.trim())
    errors.push("publicationVintage is required.");
  if (
    !entry.facilityScope.trim() ||
    !entry.nativeUnits.trim() ||
    !entry.jobDefinition.trim() ||
    !entry.timeBasis.trim() ||
    !entry.transformation.trim()
  ) {
    errors.push(
      "scope, units, job definition, time basis, and transformation are required.",
    );
  }
  if (entry.permittedUses.length === 0)
    errors.push("at least one permitted use is required.");
  if (entry.limitations.length === 0)
    errors.push("at least one limitation is required.");
  if (entry.coefficient.kind === "operations-coverage-floor") {
    const coefficient = entry.coefficient;
    if (
      !(coefficient.onsitePositionsLow > 0) ||
      coefficient.onsitePositionsHigh < coefficient.onsitePositionsLow ||
      coefficient.weeklyCoverageHours !== 168 ||
      !(coefficient.annualHoursPerFte > 0)
    ) {
      errors.push("operations coverage-floor coefficient is invalid.");
    }
  }
  if (entry.coefficient.kind === "size-band-intensity") {
    for (const band of entry.coefficient.bands) {
      if (!(band.minMwInclusive > 0))
        errors.push(`${band.id}: min MW must be positive.`);
      if (
        band.maxMwExclusive != null &&
        band.maxMwExclusive <= band.minMwInclusive
      )
        errors.push(`${band.id}: max MW must exceed min MW.`);
      if (!(band.low >= 0) || !(band.high >= band.low))
        errors.push(`${band.id}: coefficient range is invalid.`);
    }
  }
  if (
    entry.coefficient.kind === "construction-average-to-peak-ratio" &&
    (!(entry.coefficient.low > 0) ||
      entry.coefficient.high < entry.coefficient.low ||
      entry.coefficient.high > 1)
  ) {
    errors.push("construction average-to-peak range must be ordered within (0, 1].");
  }
  if (
    entry.coefficient.kind === "construction-duration-years" &&
    (!(entry.coefficient.low > 0) || entry.coefficient.high < entry.coefficient.low)
  ) {
    errors.push("construction duration range must be positive and ordered.");
  }
  return [...new Set(errors)];
}

/** Additional checks required before a candidate may drive a headline screen. */
export function validateEconomicCatalogPolicyPromotion(
  entry: EconomicSourceCatalogEntry,
): string[] {
  const errors = [...validateEconomicCatalogEntry(entry)];
  const independentPublishers = new Set(
    entry.sources.map((source) => source.publisher.trim().toLowerCase()),
  );
  if (entry.sources.length < 2 || independentPublishers.size < 2) {
    errors.push(
      "at least two independently published sources are required for a headline coefficient.",
    );
  }
  if (
    !entry.sources.some((source) => source.evidenceRole === "numeric-basis")
  ) {
    errors.push("a numeric-basis source is required.");
  }
  if (
    !entry.sources.some(
      (source) => source.evidenceRole === "independent-cross-check",
    )
  ) {
    errors.push("an independent cross-check source is required.");
  }
  if (entry.coefficient.kind === "size-band-intensity") {
    const coefficient = entry.coefficient;
    const checks = entry.sources.flatMap((source) =>
      source.evidenceRole === "independent-cross-check"
        ? (source.normalizedChecks ?? []).filter(
            (check) => check.use === coefficient.use,
          )
        : [],
    );
    for (const band of coefficient.bands) {
      const inBand = checks.filter(
        (check) =>
          check.mw != null &&
          check.units === "workers-per-utility-service-mw" &&
          check.mw >= band.minMwInclusive &&
          (band.maxMwExclusive == null || check.mw < band.maxMwExclusive),
      );
      if (inBand.length === 0) {
        errors.push(
          `${band.id}: an independent same-unit MW/jobs check is required.`,
        );
        continue;
      }
      for (const check of inBand) {
        if (check.low < band.low - 1e-9 || check.high > band.high + 1e-9) {
          errors.push(
            `${band.id}: published coefficient range does not encompass independent check at ${check.mw} MW.`,
          );
        }
      }
    }
  }
  if (
    entry.coefficient.kind === "construction-average-to-peak-ratio" ||
    entry.coefficient.kind === "construction-duration-years"
  ) {
    const coefficient = entry.coefficient;
    const checks = entry.sources.flatMap((source) =>
      (source.normalizedChecks ?? []).filter((check) => check.use === coefficient.kind),
    );
    if (checks.length < 2) {
      errors.push(`${coefficient.kind}: two normalized source checks are required.`);
    }
    for (const check of checks) {
      if (check.low < coefficient.low - 1e-9 || check.high > coefficient.high + 1e-9) {
        errors.push(`${coefficient.kind}: coefficient range does not encompass a normalized source check.`);
      }
    }
  }
  errors.push(...validateEconomicCatalogEntrySemanticAudit(entry));
  return [...new Set(errors)];
}

/**
 * Deterministically promotes candidates that pass the current policy and
 * demotes previously policy-promoted entries if a later policy rejects them.
 */
export function applyEconomicSourcePolicy(
  candidate: EconomicSourceCatalogSnapshot,
  validatedAt: string,
): EconomicSourceCatalogSnapshot {
  if (!Number.isFinite(Date.parse(validatedAt)))
    throw new Error("validatedAt must be a valid date.");
  return {
    ...candidate,
    catalogVersion: `${ECONOMIC_SOURCE_POLICY_ID}-${validatedAt}`,
    entries: candidate.entries.map((entry) => {
      const policyPasses =
        validateEconomicCatalogPolicyPromotion(entry).length === 0;
      if (policyPasses) {
        return {
          ...entry,
          lifecycle: "approved-current",
          validationPolicyId: ECONOMIC_SOURCE_POLICY_ID,
          validatedAt,
        };
      }
      if (entry.validationPolicyId || entry.lifecycle === "proposed") {
        const {
          validationPolicyId: _policyId,
          validatedAt: _validatedAt,
          ...demoted
        } = entry;
        return { ...demoted, lifecycle: "proposed" };
      }
      return entry;
    }),
  };
}

export function findRuntimeCoefficient<
  TKind extends EconomicCoefficient["kind"],
>(
  catalog: EconomicSourceCatalogSnapshot,
  kind: TKind,
  options: {
    use?: Extract<EconomicCoefficient, { kind: TKind }> extends {
      use: infer TUse;
    }
      ? TUse
      : never;
    permittedUse?: string;
    asOf?: Date;
  } = {},
):
  | (EconomicSourceCatalogEntry & {
      coefficient: Extract<EconomicCoefficient, { kind: TKind }>;
    })
  | null {
  const match = catalog.entries.find((entry) => {
    if (
      entry.coefficient.kind !== kind ||
      !isCatalogEntryRuntimeUsable(entry, options.asOf)
    )
      return false;
    if (
      options.permittedUse &&
      !entry.permittedUses.includes(options.permittedUse)
    )
      return false;
    if ("use" in entry.coefficient && options.use !== undefined)
      return entry.coefficient.use === options.use;
    return true;
  });
  return (match ?? null) as
    | (EconomicSourceCatalogEntry & {
        coefficient: Extract<EconomicCoefficient, { kind: TKind }>;
      })
    | null;
}

export function validateEconomicSourceCatalog(
  catalog: EconomicSourceCatalogSnapshot,
): string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  if (catalog.schemaVersion !== ECONOMIC_CONTRIBUTION_SCHEMA_VERSION)
    errors.push("Unsupported catalog schemaVersion.");
  if (!catalog.catalogVersion.trim())
    errors.push("catalogVersion is required.");
  for (const entry of catalog.entries) {
    if (ids.has(entry.id))
      errors.push(`Duplicate catalog entry id: ${entry.id}`);
    ids.add(entry.id);
    errors.push(
      ...validateEconomicCatalogEntry(entry).map(
        (error) => `${entry.id}: ${error}`,
      ),
    );
    if (
      (entry.lifecycle === "approved-current" ||
        entry.lifecycle === "approved-stale") &&
      !entry.reviewedBy?.trim() &&
      entry.validationPolicyId !== ECONOMIC_SOURCE_POLICY_ID
    ) {
      errors.push(
        `${entry.id}: approved entries require named review or ${ECONOMIC_SOURCE_POLICY_ID}.`,
      );
    }
  }
  return errors;
}

export const ECONOMIC_SOURCE_CATALOG = applyEconomicSourcePolicy(
  ECONOMIC_SOURCE_CATALOG_CANDIDATE,
  "2026-08-26",
);
